/**
 * Read a File and return a JPEG data URL resized so its longest edge ≤ maxEdge.
 * Strips EXIF, downsizes large photos to keep request payloads small.
 * Default 800px / quality 0.8 → typically 30–80 KB per image.
 */
export function readImageAsDataUrl(file, { maxEdge = 800, quality = 0.8 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file"));
    if (!file.type.startsWith("image/")) return reject(new Error("File is not an image"));
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) return reject(new Error("Image has zero dimensions"));
        const scale = Math.min(1, maxEdge / Math.max(w, h));
        const tw = Math.round(w * scale);
        const th = Math.round(h * scale);
        const canvas = document.createElement("canvas");
        canvas.width = tw;
        canvas.height = th;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, tw, th);
        // Always export as JPEG to keep size predictable; PNG with photos can be 5-10x larger.
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
